import Helper from "./Helper";

export function isBezierInRect(bezierObject, rect) {
  // Compute the bounding box of the bezier curve

  let { s, bezier, e } = bezierObject;

  const minX = Math.min(s[0], bezier[0], e[0]);
  const minY = Math.min(s[1], bezier[1], e[1]);
  const maxX = Math.max(s[0], bezier[0], e[0]);
  const maxY = Math.max(s[1], bezier[1], e[1]);

  // Check if the bounding box intersects the rectangle
  if (
    minX > rect.x + rect.width ||
    maxX < rect.x ||
    minY > rect.y + rect.height ||
    maxY < rect.y
  ) {
    return false;
  }

  // Check if any point on the curve is inside the rectangle
  const rectRight = rect.x + rect.width;
  const rectBottom = rect.y + rect.height;
  if (
    s[0] >= rect.x &&
    s[0] <= rectRight &&
    s[1] >= rect.y &&
    s[1] <= rectBottom
  ) {
    return true;
  }
  if (
    e[0] >= rect.x &&
    e[0] <= rectRight &&
    e[1] >= rect.y &&
    e[1] <= rectBottom
  ) {
    return true;
  }
  const cp = [2 * bezier[0] - s[0], 2 * bezier[1] - s[1]];
  if (
    cp[0] >= rect.x &&
    cp[0] <= rectRight &&
    cp[1] >= rect.y &&
    cp[1] <= rectBottom
  ) {
    return true;
  }

  // Check if the curve intersects any of the rectangle edges
  if (
    bezierLineIntersectsRect(s, bezier, rect) ||
    bezierLineIntersectsRect(bezier, e, rect)
  ) {
    return true;
  }

  // The curve is outside the rectangle and does not intersect it
  return false;
}

export function bezierLineIntersectsRect(p1, p2, rect) {
  // Check if the line intersects any of the rectangle edges
  if (
    bezierLineIntersectsLine(
      p1,
      p2,
      [rect.x, rect.y],
      [rect.x + rect.width, rect.y]
    )
  ) {
    return true;
  }
  if (
    bezierLineIntersectsLine(
      p1,
      p2,
      [rect.x + rect.width, rect.y],
      [rect.x + rect.width, rect.y + rect.height]
    )
  ) {
    return true;
  }
  if (
    bezierLineIntersectsLine(
      p1,
      p2,
      [rect.x + rect.width, rect.y + rect.height],
      [rect.x, rect.y + rect.height]
    )
  ) {
    return true;
  }
  if (
    bezierLineIntersectsLine(
      p1,
      p2,
      [rect.x, rect.y + rect.height],
      [rect.x, rect.y]
    )
  ) {
    return true;
  }
  return false;
}

export function bezierLineIntersectsLine(a, b, c, d) {
  // Compute the cross product of the vectors AB and AC
  const cross1 = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  // Compute the cross product of the vectors AB and AD
  const cross2 = (b[0] - a[0]) * (d[1] - a[1]) - (b[1] - a[1]) * (d[0] - a[0]);

  // Check if the two points are on opposite sides of the line
  if ((cross1 < 0 && cross2 > 0) || (cross1 > 0 && cross2 < 0)) {
    // Compute the cross product of the vectors CD and CA
    const cross3 =
      (d[0] - c[0]) * (a[1] - c[1]) - (d[1] - c[1]) * (a[0] - c[0]);
    // Compute the cross product of the vectors CD and CB
    const cross4 =
      (d[0] - c[0]) * (b[1] - c[1]) - (d[1] - c[1]) * (b[0] - c[0]);

    // Check if the two points are on opposite sides of the line
    if ((cross3 < 0 && cross4 > 0) || (cross3 > 0 && cross4 < 0)) {
      return true;
    }
  }

  return false;
}

export function intersectRectangle(rect1, rect2) {
  let angle = rect2.angle ?? 0;
  let center = Helper.getCenter(rect2.x, rect2.y, rect2.width, rect2.height);

  let pointsBeforeRotation = [
    [rect2.x, rect2.y],
    [rect2.x + rect2.width, rect2.y],
    [rect2.x + rect2.width, rect2.y + rect2.height],
    [rect2.x, rect2.y + rect2.height],
  ];

  let pointsAfterRotation = pointsBeforeRotation.map((point) => {
    return Helper.rotatePointAroundCenterArray(point, angle, center);
  });

  for (let point of pointsAfterRotation) {
    if (
      point[0] >= rect1.x &&
      point[0] <= rect1.x + rect1.width &&
      point[1] >= rect1.y &&
      point[1] <= rect1.y + rect1.height
    ) {
      return true;
    }
  }

  let line1 = {
    s: pointsAfterRotation[0],
    e: pointsAfterRotation[1],
  };

  let line2 = {
    s: pointsAfterRotation[1],
    e: pointsAfterRotation[2],
  };

  let line3 = {
    s: pointsAfterRotation[2],
    e: pointsAfterRotation[3],
  };

  let line4 = {
    s: pointsAfterRotation[3],
    e: pointsAfterRotation[0],
  };

  let lines = [line1, line2, line3, line4];

  for (let line of lines) {
    if (lineIntersectsRect(line, rect1)) {
      return true;
    }
  }

  return false;
}

export function lineIntersectsRect(line, rect) {
  // Define the corners of the rectangle
  const x1 = rect.x;
  const y1 = rect.y;
  const x2 = rect.x + rect.width;
  const y2 = rect.y + rect.height;

  // Check if the line is completely within the rectangle
  if (
    line.s[0] >= x1 &&
    line.s[0] <= x2 &&
    line.s[1] >= y1 &&
    line.s[1] <= y2 &&
    line.e[0] >= x1 &&
    line.e[0] <= x2 &&
    line.e[1] >= y1 &&
    line.e[1] <= y2
  ) {
    return true;
  }

  // Check if the line intersects any of the four edges of the rectangle
  const s = line.s;
  const e = line.e;
  if (lineIntersectsLine(s, e, [x1, y1], [x1, y2])) {
    return true;
  }
  if (lineIntersectsLine(s, e, [x1, y1], [x2, y1])) {
    return true;
  }
  if (lineIntersectsLine(s, e, [x2, y1], [x2, y2])) {
    return true;
  }
  if (lineIntersectsLine(s, e, [x1, y2], [x2, y2])) {
    return true;
  }
  return false;
}

export function lineIntersectsLine(line1Start, line1End, line2Start, line2End) {
  // calculate the direction of the two line segments
  const dx1 = line1End[0] - line1Start[0];
  const dy1 = line1End[1] - line1Start[1];
  const dx2 = line2End[0] - line2Start[0];
  const dy2 = line2End[1] - line2Start[1];

  // calculate the denominator for the parametric equations for the line segments
  const denominator = dx1 * dy2 - dy1 * dx2;

  // if the denominator is 0, the line segments are parallel and do not intersect
  if (denominator === 0) {
    return false;
  }

  // calculate the parameters for the parametric equations for the line segments
  const t1 =
    ((line2Start[0] - line1Start[0]) * dy2 -
      (line2Start[1] - line1Start[1]) * dx2) /
    denominator;
  const t2 =
    ((line2Start[0] - line1Start[0]) * dy1 -
      (line2Start[1] - line1Start[1]) * dx1) /
    denominator;

  // if the parameters are between 0 and 1, the line segments intersect
  if (t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1) {
    return true;
  }

  // otherwise, they do not intersect
  return false;
}

export function getPointsForCollisionCheck(
  object,
  objectSizes,
  objectSizeFactor = 1
) {
  let points = [];
  if (object.type === "arrow") {
    let tempReturn = {
      type: "lines",
      points: [],
      bezier: [],
      straights: [],
    };

    if (object.shape === 0) {
      tempReturn.straights.push({
        s: object.s,
        e: object.e,
      });
    }

    if (object.shape === 1) {
      tempReturn.bezier.push({
        s: object.s,
        bezier: object.m,
        e: object.e,
      });
    }

    if (object.shape === 2) {
      tempReturn.straights.push({
        s: object.s,
        e: object.m,
      });
      tempReturn.straights.push({
        e: object.e,
        s: object.m,
      });
    }

    if (object.shape === 3) {
      tempReturn.bezier.push({
        s: object.s,
        bezier: object.h1,
        e: object.m,
      });

      tempReturn.bezier.push({
        s: object.m,
        bezier: object.h2,
        e: object.e,
      });
    }

    ["s", "m", "e", "h1", "h2"].forEach((point) => {
      if (object[point]) {
        tempReturn.points.push(object[point]);
      }
    });

    return tempReturn;
  } else if (object.type === "object") {
    if (
      object.subType === "player" ||
      object.subType === "cone" ||
      object.subType === "handoff"
    ) {
      let boxWidth = objectSizes.viewbox.playerBoxWidth * objectSizeFactor;
      return {
        type: "rectangle",
        x: object.pos[0] - boxWidth / 2,
        y: object.pos[1] - boxWidth / 2,
        width: boxWidth,
        height: boxWidth,
        points: [
          [object.pos[0] - boxWidth / 2, object.pos[1] - boxWidth / 2],
          [object.pos[0] + boxWidth / 2, object.pos[1] - boxWidth / 2],
          [object.pos[0] + boxWidth / 2, object.pos[1] + boxWidth / 2],
          [object.pos[0] - boxWidth / 2, object.pos[1] + boxWidth / 2],
        ],
      };
    } else if (object.subType === "text" || object.subType === "rectangle") {
      let angle = object.angle ?? 0;
      let { pos, width, height } = object;

      let center = Helper.getCenter(pos[0], pos[1], width, height);

      let pointsBeforeRotation = [
        [pos[0], pos[1]],
        [pos[0] + width, pos[1]],
        [pos[0] + width, pos[1] + height],
        [pos[0], pos[1] + height],
      ];

      let pointsAfterRotation = pointsBeforeRotation.map((point) => {
        return Helper.rotatePointAroundCenterArray(point, angle, center);
      });

      return {
        type: "rectangle",
        x: pos[0],
        y: pos[1],
        width: width,
        height: height,
        points: pointsAfterRotation,
        angle: angle,
      };
    } else if (object.subType === "circle") {
      let angle = object.angle ?? 0;
      let { pos, rx, ry } = object;

      let center = pos;

      let pointsBeforeRotation = [
        [pos[0] - rx, pos[1] - ry],
        [pos[0] + rx, pos[1] - ry],
        [pos[0] + rx, pos[1] + ry],
        [pos[0] - rx, pos[1] + ry],
      ];

      let pointsAfterRotation = pointsBeforeRotation.map((point) => {
        return Helper.rotatePointAroundCenterArray(point, angle, center);
      });

      return {
        type: "rectangle",
        x: object.pos[0] - object.rx,
        y: object.pos[1] - object.ry,
        width: object.rx * 2,
        height: object.ry * 2,
        points: pointsAfterRotation,
        angle: angle,
      };
    } else if (["triangle", "polygon"].includes(object.subType)) {
      let rect = getBoundingRect(object.points);
      return {
        type: "rectangle",
        ...rect,
        points: object.points,
      };
    }
  }
  return points;
}

export function selectObjects(
  allObjects,
  origin,
  target,
  fullCourt,
  objectSizes,
  objectSizeFactor = 1
) {
  let collisionObjects = [];

  let pointsToCheck = getAllPointsForCollisionCheck(
    allObjects,
    objectSizes,
    objectSizeFactor
  );

  // get rectange coordinates from selectionBoxOrigin and selectionBoxTarget

  let selectionBoxRect = fullCourt
    ? {
        x: -Math.max(origin[1], target[1]),
        y: Math.min(origin[0], target[0]),
        width: Math.abs(target[1] - origin[1]),
        height: Math.abs(target[0] - origin[0]),
      }
    : {
        x: Math.min(origin[0], target[0]),
        y: Math.min(origin[1], target[1]),
        width: Math.abs(target[0] - origin[0]),
        height: Math.abs(target[1] - origin[1]),
      };

  //check for intersection of all points with selection box
  pointsToCheck.forEach((pointToCheck) => {
    if (pointToCheck.shape.type === "rectangle") {
      let rect2 = {
        x: pointToCheck.shape.x,
        y: pointToCheck.shape.y,
        width: pointToCheck.shape.width,
        height: pointToCheck.shape.height,
        angle: pointToCheck.shape.angle,
      };
      if (intersectRectangle(selectionBoxRect, rect2)) {
        collisionObjects.push(pointToCheck.id);
      }
    }

    if (pointToCheck.shape.type === "lines") {
      let { straights, bezier } = pointToCheck.shape;
      straights.forEach((straight) => {
        if (lineIntersectsRect(straight, selectionBoxRect)) {
          collisionObjects.push(pointToCheck.id);
        }
      });

      bezier.forEach((bezier) => {
        if (isBezierInRect(bezier, selectionBoxRect)) {
          collisionObjects.push(pointToCheck.id);
        }
      });
    }
  });
  return collisionObjects;
}

export function getAllPointsForCollisionCheck(
  objects,
  objectSizes,
  objectSizeFactor = 1
) {
  let pointsToCheck = [];
  objects.forEach((object) => {
    let shape = getPointsForCollisionCheck(
      object,
      objectSizes,
      objectSizeFactor
    );

    pointsToCheck.push({
      shape: shape,
      id: object.id,
    });
  });
  return pointsToCheck;
}

export function getSelectionRect(
  selectedObjects,
  objectSizes,
  fullCourt,
  minAmount = 2,
  objectSizeFactor = 1
) {
  //return null;

  if (selectedObjects.length < minAmount) {
    return null;
  }

  let allObjectsWithPoints = getAllPointsForCollisionCheck(
    selectedObjects,
    objectSizes,
    objectSizeFactor
  );

  let allPoints = allObjectsWithPoints.reduce((acc, curr) => {
    return [...acc, ...curr.shape.points];
  }, []);

  let rect = getBoundingRect(allPoints);

  return fullCourt
    ? {
        x: rect.y,
        y: -rect.x - rect.width,
        width: rect.height,
        height: rect.width,
      }
    : rect;
}

export function getBoundingRect(points, padding = 0) {
  var minX = Infinity;
  var minY = Infinity;
  var maxX = -Infinity;
  var maxY = -Infinity;

  points.forEach((point) => {
    if (point[0] < minX) {
      minX = point[0];
    }
    if (point[0] > maxX) {
      maxX = point[0];
    }
    if (point[1] < minY) {
      minY = point[1];
    }
    if (point[1] > maxY) {
      maxY = point[1];
    }
  });

  minX -= padding;
  minY -= padding;
  maxX += padding;
  maxY += padding;

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
